import React, { type ReactNode } from "react";
import { AspectRatio, Box, Flex, type LayoutProps, type SpaceProps } from "@chakra-ui/react";
import { type StaticImageData } from "next/image";
import { DecorativeBackgroundImage, FillImage } from "components/images";
import sunsetImage from "../../public/assets/product-showcase/sunset.png";
import duskImage from "../../public/assets/product-showcase/dusk.png";
import coastalImage from "../../public/assets/product-showcase/coastal.png";
import mustardImage from "../../public/assets/product-showcase/mustard.png";
import poppyImage from "../../public/assets/product-showcase/poppy.png";
import goldImage from "../../public/assets/product-showcase/gold.png";
import desertImage from "../../public/assets/product-showcase/desert.png";
import autumnImage from "../../public/assets/product-showcase/autumn.png";
import { progressTabMediaRatio } from "./tabs";

const dropShadowMd =
  "drop-shadow(0px 34.807px 20.627px rgba(0, 0, 0, 0.02)) drop-shadow(0px 15.47px 15.47px rgba(0, 0, 0, 0.04)) drop-shadow(0px 3.867px 7.735px rgba(0, 0, 0, 0.05))";

export type ProductShowcaseProps = {
  /**
   * The variant determines which blurred background image to use.
   * @default "sunset"
   */
  variant?: "sunset" | "dusk" | "coastal" | "mustard" | "poppy" | "gold" | "desert" | "autumn";

  /**
   * Whether to mark the showcase background images as `priority` to avoid the visual
   * jump when the actual image loads in place of the blur placeholder.
   */
  isPriority?: boolean;

  /**
   * Product screen in the form of either an image or animation.
   * Displayed on `sm` screens and larger.
   */
  productScreen: ReactNode;

  /**
   * Displayed on the smallest screens.
   */
  mobileProductScreen?: ReactNode;

  hasBorder?: boolean;
} & SpaceProps &
  LayoutProps;

export function ProductShowcase({
  variant = "sunset",
  isPriority,
  productScreen,
  mobileProductScreen,
  hasBorder,
  ...props
}: ProductShowcaseProps) {
  let backgroundImage;

  switch (variant) {
    case "dusk": {
      backgroundImage = duskImage;
      break;
    }

    case "coastal": {
      backgroundImage = coastalImage;
      break;
    }

    case "mustard": {
      backgroundImage = mustardImage;
      break;
    }

    case "poppy": {
      backgroundImage = poppyImage;
      break;
    }

    case "gold": {
      backgroundImage = goldImage;
      break;
    }

    case "desert": {
      backgroundImage = desertImage;
      break;
    }

    case "autumn": {
      backgroundImage = autumnImage;
      break;
    }

    default: {
      backgroundImage = sunsetImage;
    }
  }

  return (
    <AspectRatio
      borderRadius="small"
      ratio={progressTabMediaRatio}
      overflow="hidden"
      w="100%"
      border={hasBorder ? "1px" : "none"}
      borderColor="brand.border.extraLight"
      {...props}
    >
      <Flex align="end" boxSize="100%" pos="relative">
        <DecorativeBackgroundImage priority={isPriority} src={backgroundImage} style={{ objectFit: "cover" }} />

        <Box pos="absolute" bottom={0} left={{ base: "12%", sm: 0 }} top={{ base: "12%", sm: "8%" }} right={0}>
          <Box boxSize="100%" mt="auto" ml="auto" mr={{ base: "0", sm: "auto" }} filter={dropShadowMd}>
            <AspectRatio
              display={{ base: "none", sm: "block" }}
              ratio={1008 / 537}
              boxSize="100%"
              maxW={{ base: "94%", md: "78%", xl: "920px" }}
              mx="auto"
              pos="relative"
            >
              {productScreen}
            </AspectRatio>
            <Box display={{ base: "block", sm: "none" }} boxSize="100%" pos="relative">
              {mobileProductScreen}
            </Box>
          </Box>
        </Box>
      </Flex>
    </AspectRatio>
  );
}

type ImageProps = {
  isPriority?: boolean;
  isMobile?: boolean;
  src: StaticImageData;
  alt: string;
};

/** Helper component to use for the ProductScreen when using a static image. */
export function ProductShowcaseImage({ isPriority, isMobile, src, alt }: ImageProps) {
  return (
    <FillImage
      priority={isPriority}
      src={src}
      alt={alt}
      style={{
        borderTopLeftRadius: "8px",
        borderTopRightRadius: isMobile ? "none" : "8px",
        objectFit: "cover", // Using `contain` will create a flicker effect when the placeholder is shown
        objectPosition: isMobile ? "bottom left" : "top center",
      }}
    />
  );
}
